/*
 * /myschoolrx/experts — Public Experts page.
 *
 * 6/4 update — content now loads from the Caporilli product CMS
 * (`section_content.experts`) so Super Admin can edit copy / bios /
 * photo URLs / order / add new experts without code changes. We keep
 * a hardcoded EXPERTS_FALLBACK so the page still renders if the
 * backend is unreachable.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Mail, GraduationCap, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PublicPageShell, BLUE, BLUE_HOVER } from "./publicChrome";

const API = process.env.REACT_APP_BACKEND_URL;

const EXPERTS_FALLBACK = [
  {
    id: "dirk",
    name: "Dirk DaCosta CTO, CAIO",
    title: "Co-Founder, 589 Labs",
    organization: "589 Labs",
    bio:
      "Builds the technology platform behind MySchoolRx. Focused on giving K-12 school leaders Executive-Clarity data products that turn into board decisions, not file-cabinet PDFs.",
    expertise: [
      "Executive analytics for school leaders",
      "Diagnostic platform engineering",
      "Board-ready reporting",
      "Multi-tenant SaaS architecture",
    ],
    credentials: ["Co-Founder, 589 Labs", "Executive-Clarity Product Lead"],
    experience:
      "Leads 589 Labs' work on MySchoolRx, NJEA Oversight Portal, and the Clarity Assessments suite.",
    initials: "DD",
    color: "#3B4FE4",
    photo: "/assets/dacosta.webp",
    order: 2,
    active: true,
  },
];

export const ExpertAvatar = ({ exp, className = "h-24 w-24 sm:h-28 sm:w-28" }) => {
  const fx = Number.isFinite(exp?.img_focal_x) ? exp.img_focal_x : 50;
  const fy = Number.isFinite(exp?.img_focal_y) ? exp.img_focal_y : 50;
  const zoom = Number(exp?.img_zoom) > 0 ? Number(exp.img_zoom) : 1;
  if (!exp?.photo) {
    return (
      <div className={`${className} rounded-full flex items-center justify-center text-white text-[28px] font-bold shrink-0`}
        style={{ background: exp?.color || "#0b1220" }} aria-hidden data-testid={`expert-initials-${exp?.id}`}>
        {exp?.initials || "?"}
      </div>
    );
  }
  return (
    <div className={`${className} rounded-full overflow-hidden shrink-0 border border-[#e5e7eb] shadow-sm bg-[#f3f4f6]`} data-testid={`expert-photo-${exp.id}`}>
      <img src={exp.photo} alt={exp.name} className="h-full w-full object-cover"
        style={{ objectPosition: `${fx}% ${fy}%`, transform: `scale(${zoom})` }} />
    </div>
  );
};

const ExpertCard = ({ exp }) => (
  <article
    className="rounded-2xl border border-[#e5e7eb] bg-white p-6 lg:p-7 shadow-sm hover:shadow-md transition-shadow"
    data-testid={`expert-card-${exp.id}`}
  >
    <div className="flex items-start gap-4">
      <ExpertAvatar exp={exp} />
      <div className="min-w-0">
        <h3 className="text-[21px] sm:text-[22px] font-bold text-[#111827] leading-tight">{exp.name}</h3>
        <div className="text-[17px] text-[#6b7280] mt-0.5 leading-snug">{exp.title}</div>
        {exp.organization && (
          <div className="text-[16px] text-[#3B4FE4] mt-0.5 inline-flex items-center gap-1.5">
            <Building2 className="h-3 w-3" /> {exp.organization}
          </div>
        )}
      </div>
    </div>

    <p className="mt-4 text-[18px] text-[#374151] leading-relaxed">{exp.bio}</p>

    {Array.isArray(exp.expertise) && exp.expertise.length > 0 && (
      <div className="mt-5">
        <div className="text-[14px] uppercase tracking-wider font-semibold text-[#6b7280] mb-2">Areas of expertise</div>
        <div className="flex flex-wrap gap-1.5">
          {exp.expertise.map((e, i) => (
            <span key={i} className="inline-flex items-center px-2.5 py-1 rounded-full bg-[#f3f4f6] text-[#374151] text-[15px] font-medium">{e}</span>
          ))}
        </div>
      </div>
    )}

    {(Array.isArray(exp.credentials) && exp.credentials.length > 0) || exp.experience ? (
      <div className="mt-4 pt-4 border-t border-[#e5e7eb] grid grid-cols-1 sm:grid-cols-2 gap-3">
        {Array.isArray(exp.credentials) && exp.credentials.length > 0 && (
          <div>
            <div className="text-[14px] uppercase tracking-wider font-semibold text-[#6b7280] flex items-center gap-1">
              <GraduationCap className="h-3 w-3" /> Credentials
            </div>
            <ul className="mt-1.5 text-[16px] text-[#374151] space-y-0.5">
              {exp.credentials.map((c, i) => (<li key={i}>· {c}</li>))}
            </ul>
          </div>
        )}
        {exp.experience && (
          <div>
            <div className="text-[14px] uppercase tracking-wider font-semibold text-[#6b7280]">Experience</div>
            <p className="mt-1.5 text-[16px] text-[#374151] leading-snug">{exp.experience}</p>
          </div>
        )}
      </div>
    ) : null}
  </article>
);

const ExpertsPage = () => {
  const navigate = useNavigate();
  const [cms, setCms] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/products/by-route?route=/myschoolrx`)
      .then((r) => setCms(r.data?.section_content?.experts || null))
      .catch(() => setCms(null));
  }, []);

  const experts = useMemo(() => {
    const src = Array.isArray(cms?.list) && cms.list.length > 0 ? cms.list : EXPERTS_FALLBACK;
    return [...src]
      .filter((e) => e.active !== false)
      .sort((a, b) => (a.order || 99) - (b.order || 99));
  }, [cms]);

  const headline = cms?.headline || "Meet the Experts Behind MySchoolRx";
  const subheadline = cms?.subheadline ||
    "MySchoolRx gives your board a credible, board-ready picture of school quality and risk. After completing the diagnostic, schools can request expert support to interpret results, prioritize corrective action, and accelerate operational improvement.";
  const rosterNote = cms?.roster_note || "More experts will be added as MySchoolRx network grows.";
  const ctaLabel = cms?.cta_label || "Request Expert Support";
  const ctaDest = cms?.cta_destination || "/myschoolrx/signup";
  const bottomHeading = cms?.bottom_cta_heading || "Need expert support after your diagnostic?";
  const bottomBody = cms?.bottom_cta_body ||
    "Once your diagnostic is complete, you can request expert support to interpret risk levels, build a corrective action plan, or prepare for an upcoming annual report submission.";

  return (
    <PublicPageShell
      title="Research & Experts — MySchoolRx | MySchoolRx"
      description={subheadline}
      canonicalPath="/myschoolrx/experts"
      testid="experts-page"
    >
      {/* Hero */}
      <section className="bg-[#f9fbfc] border-b border-[#e5e7eb] py-14 lg:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
          <span className="text-[14px] uppercase tracking-[0.18em] font-semibold" style={{ color: BLUE }}>Research &amp; Experts</span>
          <h1 className="mt-3 text-4xl sm:text-5xl font-bold text-[#111827] leading-[1.08] tracking-tight max-w-4xl" data-testid="experts-headline">{headline}</h1>
          <p className="mt-5 text-[20px] text-[#4b5563] leading-relaxed max-w-3xl" data-testid="experts-subheadline">{subheadline}</p>
        </div>
      </section>

      {/* Cards */}
      <section className="py-14 lg:py-20 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-6" data-testid="experts-grid">
            {experts.map((exp) => (<ExpertCard key={exp.id} exp={exp} />))}
          </div>
          <p className="mt-6 text-[16px] text-[#6b7280] text-center" data-testid="experts-roster-note">{rosterNote}</p>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-[#0b1220] text-white py-14 lg:py-20">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-10 text-center">
          <h2 className="text-[30px] lg:text-4xl font-extrabold leading-tight" data-testid="experts-bottom-heading">{bottomHeading}</h2>
          <p className="mt-3 text-[18px] text-white/70 leading-relaxed">{bottomBody}</p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              onClick={() => navigate(ctaDest)}
              className="h-12 text-[17px] text-white px-6"
              style={{ backgroundColor: BLUE }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = BLUE_HOVER)}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = BLUE)}
              data-testid="experts-cta-primary"
            >
              {ctaLabel} <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
            <a href="/contact" className="inline-flex items-center justify-center gap-1.5 px-5 py-2 rounded-md border border-white/30 text-[17px] font-semibold text-white/90 hover:bg-white/10" data-testid="experts-cta-email">
              <Mail className="h-4 w-4" /> Contact our team
            </a>
          </div>
        </div>
      </section>
    </PublicPageShell>
  );
};

export default ExpertsPage;
